import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { DataIssueBanner, DataTable, UpdatedStamp } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { signTextClass } from "@/lib/tone";
import { dataReference, partialMonths, trimPartialTrailing } from "@/lib/time-range";

import { getBreakout, messageFor, UnitsSoldError, type SeriesPoint, type TtuBreakout, type TtuProduct, type TtuSeries } from "./api";
import { AttentionList } from "./components/AttentionList";
import { BreakoutSkeleton } from "./components/LoadingSkeletons";
import { NotComputedCard } from "./components/NotComputedCard";
import { ScopeNote } from "./components/ScopeNote";
import { StepHeadline } from "./components/StepHeadline";
import { ModuleControls, regionConflictNote, sourceScopeNote, useControls, type Filter } from "./controls";
import { channelLabel, emptyCopy, formatTtuPct, formatTtuValue, type Grain } from "./format";

export function TrafficPage({ areaName }: { areaName: string }) { return <BreakoutPage step="traffic" areaName={areaName} />; }

export function breakoutPoints(
  series: TtuSeries,
  grain: Grain,
  dataThrough: string | null,
): SeriesPoint[] {
  const points = series[grain].filter((point) => point.present);
  if (!validDataThrough(dataThrough)) return points;
  const reference = chartReference(dataThrough, null);
  const trimmed = trimPartialTrailing(points, grain, reference);
  return trimmed.length < 2 ? points : trimmed;
}

export function chartReference(dataThrough: string | null, generatedAt: string | null): Date {
  if (!validDataThrough(dataThrough)) return dataReference(null, generatedAt);
  const reference = new Date(`${dataThrough}T00:00:00Z`);
  reference.setUTCDate(reference.getUTCDate() + 1);
  return reference;
}

function validDataThrough(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function BreakoutPage({ step, areaName, extra }: { step: "traffic" | "conversion"; areaName: string; extra?: ReactNode }) {
  const controls = useControls();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const result = useQuery({ queryKey: ["meteoric_units_sold", step, controls.toQuery()], queryFn: () => getBreakout(step, controls.toQuery()) });
  const data = result.data?.data;
  return <div className="flex flex-col gap-6"><div className="flex flex-wrap items-center justify-between gap-3"><ModuleControls controls={controls} />{data && <UpdatedStamp generatedAt={data.generated_at} />}</div>
    {result.isPending ? <BreakoutSkeleton /> : result.isError ? result.error instanceof UnitsSoldError && result.error.status === 404 ? <NotComputedCard /> : <Alert><AlertTitle>{step === "traffic" ? "Traffic" : "Conversion"} unavailable</AlertTitle><AlertDescription>{messageFor(result.error, areaName)}</AlertDescription></Alert> : data && <BreakoutBody
      data={data}
      controls={controls}
      search={search}
      headlineLabel={step === "traffic" ? "Traffic" : "Conversion rate"}
      extra={extra}
    />}
  </div>;
}

export function BreakoutBody({
  data,
  controls,
  search,
  headlineLabel,
  extra,
  unitsFiltered = false,
  selectedProduct,
  header,
}: {
  data: TtuBreakout;
  controls: ReturnType<typeof useControls>;
  search: Record<string, unknown>;
  headlineLabel: string;
  extra?: ReactNode;
  unitsFiltered?: boolean;
  selectedProduct?: string | null;
  header?: (conflict: boolean) => ReactNode;
}) {
  const conflictNote = regionConflictNote(controls.filter, data.scope);
  const sourceNote = sourceScopeNote(controls.filter, data.scope.excluded_channel);
  if (conflictNote) return <>
    {header?.(true)}
    <ScopeNote>{conflictNote}</ScopeNote>
  </>;
  const reference = chartReference(data.headline.data_through, data.generated_at);
  const yoyPoints = breakoutPoints(data.series_yoy, controls.grain, data.headline.data_through);
  const absolutePoints = breakoutPoints(data.series_abs, controls.grain, data.headline.data_through);
  const monthIssues = controls.grain === "monthly"
    ? partialMonths(data.series_abs.monthly, reference).map((issue) => ({
      month: issue.month,
      kind: issue.kind,
      message: issue.kind === "partial"
        ? openMonthMessage(
          issue.month,
          !absolutePoints.some((point) => point.m === issue.month)
            && !yoyPoints.some((point) => point.m === issue.month)
            && absolutePoints.length > 0
            ? absolutePoints.at(-1)!.m
            : null,
          { dataThrough: data.headline.data_through, generatedAt: data.generated_at },
        )
        : staleMonthMessage(issue.month),
    }))
    : [];
  const partialIssues = monthIssues.filter((issue) => issue.kind === "partial");
  const staleIssues = monthIssues.filter((issue) => issue.kind === "stale");
  return <>
    {header?.(false)}
    <>
      {sourceNote && <ScopeNote>{sourceNote}</ScopeNote>}
      {data.scope.note && <ScopeNote>{data.scope.note}</ScopeNote>}
      {partialIssues.length > 0 && <ScopeNote>{partialIssues.map((issue) => issue.message).join("; ")}</ScopeNote>}
      <DataIssueBanner issues={staleIssues} />
      <StepHeadline label={headlineLabel} headline={data.headline} yoyPoints={yoyPoints} absolutePoints={absolutePoints} grain={controls.grain} />
      {extra}
      {data.headline.measured && <BreakoutTable
        data={data}
        overview={controls.filter.region === "overview"}
        unitsFiltered={unitsFiltered}
        selectedProduct={selectedProduct}
        emptyMessage={emptyBreakoutMessage(data, controls.filter, controls.channels)}
      />}
      <AttentionList items={data.attention} search={search} />
    </>
  </>;
}

export function emptyBreakoutMessage(data: TtuBreakout, filter: Filter, channels = filter.channel): string | null {
  if (!data.headline.measured) return null;
  const productRows = data.products.filter((row) => row.name !== "TOTAL");
  const hasValue = filter.product
    ? productRows.some((row) => row.name === filter.product && row.value !== null)
    : productRows.some((row) => row.value !== null);
  if (hasValue) return null;
  return emptyCopy(data.step === "units" ? "sales" : "sessions", filter.product, channels);
}

export function openMonthMessage(
  month: string,
  chartsEndAt: string | null,
  dates: { dataThrough: string | null; generatedAt: string | null },
): string {
  const parsed = parseMonth(month);
  const label = monthLabel(month, false);
  const generatedMonth = validDate(dates.generatedAt)?.toISOString().slice(0, 7) ?? null;
  const todayMonth = new Date().toISOString().slice(0, 7);
  const closed = parsed !== null && (generatedMonth !== null && generatedMonth > month || todayMonth > month);
  const endLabel = chartsEndAt ? monthLabel(chartsEndAt, month.slice(0, 4) !== chartsEndAt.slice(0, 4)) : null;

  if (closed) {
    const through = validDataThrough(dates.dataThrough)
      ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
        .format(new Date(`${dates.dataThrough}T00:00:00Z`))
      : dates.dataThrough ?? "an unknown date";
    return endLabel
      ? `${label} has closed but its data stops at ${through}, so the charts end at ${endLabel}.`
      : `${label} has closed but its data stops at ${through}, so its figure is partial.`;
  }
  return endLabel
    ? `${label} is still open, so the charts end at ${endLabel} until the month closes.`
    : `${label} is still open, so its figure is partial.`;
}

export function staleMonthMessage(month: string): string {
  return `${monthLabel(month, false)} has closed but is missing from this series.`;
}

function parseMonth(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const parsed = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return parsed.toISOString().slice(0, 7) === value ? parsed : null;
}

function monthLabel(value: string, includeYear: boolean): string {
  const parsed = parseMonth(value);
  return parsed
    ? new Intl.DateTimeFormat("en-US", { month: "long", ...(includeYear ? { year: "numeric" } : {}), timeZone: "UTC" }).format(parsed)
    : value;
}

function validDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function BreakoutTable({ data, overview, unitsFiltered = false, selectedProduct, emptyMessage }: { data: TtuBreakout; overview: boolean; unitsFiltered?: boolean; selectedProduct?: string | null; emptyMessage?: string | null }) {
  const rows = [...data.products.filter((row) => row.name !== "TOTAL"), ...data.products.filter((row) => row.name === "TOTAL")];
  const columns: ColumnDef<TtuProduct, unknown>[] = [
    { accessorKey: "name", header: "Product", meta: { className: "sticky left-0 bg-ks-surface in-[.bg-ks-purple-4]:bg-ks-purple-4" }, cell: ({ row }) => <span className="inline-flex items-center gap-2">{row.original.name === "TOTAL" && unitsFiltered ? "TOTAL (own-brand products)" : row.original.name}{row.original.low_confidence && <Badge variant="outline">Low sessions</Badge>}</span> },
    { accessorKey: "value", header: data.step === "traffic" ? "Sessions" : data.step === "conversion" ? "CVR" : "Units", cell: ({ row }) => formatTtuValue(row.original.value, data.headline.unit) },
    { accessorKey: "wow_pct", header: "WoW", cell: ({ row }) => deltaCell(row.original.wow_pct) },
    { accessorKey: "mom_pct", header: "MoM", cell: ({ row }) => deltaCell(row.original.mom_pct) },
    { accessorKey: "yoy_pct", header: "YoY", cell: ({ row }) => deltaCell(row.original.yoy_pct) },
    ...(overview ? [{ accessorKey: "top_marketplace", header: "Top marketplace", cell: ({ row }: { row: { original: TtuProduct } }) => row.original.top_marketplace ? channelLabel(row.original.top_marketplace) : "–" } as ColumnDef<TtuProduct, unknown>] : []),
  ];
  if (emptyMessage) return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  return <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2"><DataTable columns={columns} rows={rows} page={{ pageIndex: 0, pageSize: Math.max(1, rows.length), rowCount: rows.length }} onPageChange={() => {}} caption={`${data.step} by product`} rowClassName={(row) => row.name === selectedProduct ? "bg-ks-purple-4" : row.name === "TOTAL" ? "font-semibold border-t border-border" : undefined} />{unitsFiltered && <p className="text-xs text-muted-foreground">This filtered view counts own-brand products only. Clear the filters to see third-party products.</p>}</div>;
}

function deltaCell(value: number | null) {
  return value === null ? "–" : <span className={signTextClass(value)}>{formatTtuPct(value)}</span>;
}
